import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import ManagerMobileHeader from '../manager-dashboard/components/ManagerMobileHeader';
import ManagerSidebar from '../manager-dashboard/components/ManagerSidebar';
import ManagerBottomNav from '../manager-dashboard/components/ManagerBottomNav';
import SummaryCards from '../performance-actuelle/components/SummaryCards';
import PerfDailyTrendChart from '../performance-actuelle/components/PerfDailyTrendChart';
import ManagerComparisonChart from '../performance-actuelle/components/ManagerComparisonChart';
import ServerObjectivesSection from '../performance-actuelle/components/ServerObjectivesSection';
import ManagerObjectivesTable from '../performance-actuelle/components/ManagerObjectivesTable';
import CreatorCurrentTable from '../performance-actuelle/components/CreatorCurrentTable';
import ObjectifsADateSection from '../performance-actuelle/components/ObjectifsADateSection';
import OpportunitesBonusSection from '../performance-actuelle/components/OpportunitesBonusSection';

const FALLBACK_PALIERS = [
  { pct: 70, bonus: 2 }, { pct: 75, bonus: 3 }, { pct: 80, bonus: 4 },
  { pct: 85, bonus: 5 }, { pct: 90, bonus: 7 }, { pct: 95, bonus: 8 },
  { pct: 100, bonus: 9 }, { pct: 105, bonus: 10 }, { pct: 110, bonus: 11 },
  { pct: 115, bonus: 12 }, { pct: 120, bonus: 13 }, { pct: 125, bonus: 14 }, { pct: 130, bonus: 15 },
];

const showToast = (msg, type = 'error') => {
  const el = document.createElement('div');
  el.textContent = msg;
  el.style.cssText = `
    position:fixed;bottom:24px;right:24px;z-index:9999;
    padding:12px 20px;border-radius:10px;font-size:13px;font-family:Inter,sans-serif;
    background:${type === 'error' ? '#1a0000' : '#001a00'};
    color:${type === 'error' ? '#ff4444' : '#22c55e'};
    border:1px solid ${type === 'error' ? 'rgba(229,32,42,0.3)' : 'rgba(34,197,94,0.3)'};
    box-shadow:0 4px 20px rgba(0,0,0,0.5);
  `;
  document.body?.appendChild(el);
  setTimeout(() => el?.remove(), 4000);
};

const SectionHeader = ({ color, label }) => (
  <div className="flex items-center gap-2 mb-4">
    <div className="w-1 h-4 rounded-full" style={{ backgroundColor: color }} />
    <h2
      className="text-xs font-semibold uppercase tracking-widest"
      style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}
    >
      {label}
    </h2>
  </div>
);

const ManagerRegionPerformance = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [currentPeriod, setCurrentPeriod] = useState('');
  const [serversData, setServersData] = useState([]);
  const [managers, setManagers] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [incrementalConfig, setIncrementalConfig] = useState([]);
  const [managerObjectives, setManagerObjectives] = useState([]);
  const [dailyTrendData, setDailyTrendData] = useState([]);
  const [lastDailyDate, setLastDailyDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(true);

  const [managerProfile, setManagerProfile] = useState(null);
  const [allowedServers, setAllowedServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState('');
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');

  // ── Fetch period ──────────────────────────────────────────────────────────
  const fetchPeriod = useCallback(async () => {
    try {
      const { data: periodData, error } = await supabase?.from('creator_current_month')?.select('period')?.order('updated_at', { ascending: false })?.limit(1)?.single();
      if (error && error?.code !== 'PGRST116') console.error('Period fetch error:', error);
      return periodData?.period || `${new Date()?.getFullYear()}-${String(new Date()?.getMonth() + 1)?.padStart(2, '0')}`;
    } catch (err) {
      return `${new Date()?.getFullYear()}-${String(new Date()?.getMonth() + 1)?.padStart(2, '0')}`;
    }
  }, []);

  // ── Fetch config ──────────────────────────────────────────────────────────
  const fetchConfig = useCallback(async (period, servers) => {
    setConfigLoading(true);
    try {
      const [serversRes, incrementalRes] = await Promise.all([
        supabase?.from('servers')?.select('id, name, color')?.eq('is_active', true)?.in('id', servers),
        supabase?.from('incremental_config')?.select('server, target_100pct, completion_tiers')?.eq('period', period)?.in('server', servers),
      ]);
      if (!serversRes?.error) setServersData(serversRes?.data || []);
      if (!incrementalRes?.error) setIncrementalConfig(incrementalRes?.data || []);
    } catch (err) {
      showToast('Erreur chargement configuration');
    } finally {
      setConfigLoading(false);
    }
  }, []);

  // ── Fetch managers ────────────────────────────────────────────────────────
  const fetchManagers = useCallback(async () => {
    try {
      const { data, error } = await supabase?.from('users')?.select('id, name')?.in('role', ['manager', 'region_manager'])?.order('name');
      if (!error) setManagers(data || []);
    } catch (err) {
      // silent
    }
  }, []);

  // ── Fetch main data ───────────────────────────────────────────────────────
  const fetchCurrentData = useCallback(async (period, server, managerId, servers) => {
    setLoading(true);
    try {
      let query = supabase?.from('creator_current_month')?.select('tiktok_id, username, server, manager_name, manager_id, diamonds, tier_current, tier_m1, activeness_level, bonus_rankup, bonus_activeness, is_active, is_new_recruit, cashback_amount, cashback_pct, period, days_elapsed, updated_at, tier_status, live_days, live_duration_minutes, objective_diamonds, gap_to_objective, rankup_eligible, projected_diamonds')?.eq('period', period)?.in('server', servers);
      if (server !== 'Tous') query = query?.eq('server', server);
      if (managerId) query = query?.eq('manager_id', managerId);
      const { data, error } = await query;
      if (!error) {
        // Enrichir avec level + display_name depuis creators
        const tiktokIds = (data || [])?.map(r => r?.tiktok_id)?.filter(Boolean);
        let creatorsMap = {};
        if (tiktokIds?.length > 0) {
          const { data: creatorsData } = await supabase?.from('creators')?.select('tiktok_id, level, display_name')?.in('tiktok_id', tiktokIds);
          creatorsData?.forEach(c => { creatorsMap[c?.tiktok_id] = c; });
        }
        setCurrentData((data || [])?.map(r => ({
          ...r,
          level: creatorsMap[r?.tiktok_id]?.level || null,
          display_name: creatorsMap[r?.tiktok_id]?.display_name || null,
        })));
      } else setCurrentData([]);
    } catch (err) {
      setCurrentData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch manager objectives ──────────────────────────────────────────────
  const fetchManagerObjectives = useCallback(async (period, servers) => {
    try {
      const { data, error } = await supabase?.from('manager_objectives')?.select(`
        manager_id, server, objective_diamonds, diamonds_m1,
        users!manager_objectives_manager_id_fkey(id, name)
      `)?.eq('period', period)?.in('server', servers);
      if (!error) setManagerObjectives(data || []);
    } catch (err) {
      // silent
    }
  }, []);

  // ── Fetch daily trend ─────────────────────────────────────────────────────
  const fetchDailyTrend = useCallback(async (period, server, servers) => {
    try {
      let allDailyTrend = [];
      let page = 0;
      while (true) {
        const [pY, pM] = period.split('-').map(Number);
        const lastDay = new Date(pY, pM, 0).getDate();
        let query = supabase?.from('creator_daily')?.select('date, diamonds, server, live_streams')?.gte('date', `${period}-01`)?.lte('date', `${period}-${String(lastDay).padStart(2, '0')}`)?.in('server', servers)?.order('date')?.range(page * 1000, (page + 1) * 1000 - 1);
        const { data, error } = await query;
        if (error) break;
        if (!data || data?.length === 0) break;
        allDailyTrend = [...allDailyTrend, ...data];
        if (data?.length < 1000) break;
        page++;
      }
      const filteredTrend = server !== 'Tous'
        ? allDailyTrend?.filter((r) => r?.server === server)
        : allDailyTrend;
      const trendByDate = {};
      filteredTrend?.forEach((r) => {
        if (!trendByDate?.[r?.date]) trendByDate[r.date] = { date: r?.date, diamonds: 0, live_streams: 0 };
        trendByDate[r.date].diamonds += r?.diamonds || 0;
        trendByDate[r.date].live_streams += r?.live_streams || 0;
      });
      const trendData = Object.values(trendByDate)?.sort((a, b) => a?.date?.localeCompare(b?.date));
      setDailyTrendData(trendData);
    } catch (err) {
      // silent
    }
  }, []);

  const fetchLastDailyDate = useCallback(async () => {
    try {
      const { data } = await supabase?.from('creator_daily')?.select('date')?.order('date', { ascending: false })?.limit(1)?.single();
      if (data?.date) setLastDailyDate(data?.date);
    } catch (err) {
      // ignore
    }
  }, []);

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) { navigate('/login-authentication'); return; }

        const { data: profile } = await supabase?.from('users')?.select('responsible_servers, name, role')?.eq('id', user?.id)?.single();

        const servers = profile?.responsible_servers || [];
        setManagerProfile(profile);
        setAllowedServers(servers);

        if (servers?.length === 0) {
          setLoading(false);
          setConfigLoading(false);
          return;
        }

        const defaultServer = servers?.length === 1 ? servers?.[0] : 'Tous';
        setSelectedServer(defaultServer);

        const period = await fetchPeriod();
        setCurrentPeriod(period);

        await Promise.all([
          fetchConfig(period, servers),
          fetchManagers(),
          fetchManagerObjectives(period, servers),
          fetchLastDailyDate(),
        ]);

        await fetchCurrentData(period, defaultServer, '', servers);
        await fetchDailyTrend(period, defaultServer, servers);
      } catch (e) {
        console.error('Region performance load error:', e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // ── Re-fetch when filters change ──────────────────────────────────────────
  useEffect(() => {
    if (!currentPeriod || allowedServers?.length === 0) return;
    fetchCurrentData(currentPeriod, selectedServer, selectedManagerId, allowedServers);
    fetchDailyTrend(currentPeriod, selectedServer, allowedServers);
  }, [selectedServer, selectedManagerId, currentPeriod]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    if (!selectedLevel) return currentData;
    return currentData?.filter((r) => r?.level === selectedLevel);
  }, [currentData, selectedLevel]);

  const summary = useMemo(() => {
    const total_diamonds = filteredData?.reduce((sum, r) => sum + (r?.diamonds || 0), 0);
    const total_creators = filteredData?.length;
    const active_creators = filteredData?.filter((r) => r?.is_active === true)?.length;
    const go_live_rate = total_creators > 0 ? Math.round((active_creators / total_creators) * 100) : 0;
    const days_elapsed = filteredData?.reduce((max, r) => Math.max(max, r?.days_elapsed || 0), 0);
    const new_recruits = filteredData?.filter((r) => r?.is_new_recruit === true)?.length || 0;
    return { total_diamonds, total_creators, active_creators, go_live_rate, days_elapsed, last_update: lastDailyDate, new_recruits };
  }, [filteredData, lastDailyDate]);

  const targets = useMemo(() => {
    const map = {};
    incrementalConfig?.forEach((r) => { if (r?.server) map[r.server] = r?.target_100pct || 0; });
    return map;
  }, [incrementalConfig]);

  const paliersMap = useMemo(() => {
    const map = {};
    incrementalConfig?.forEach((r) => {
      if (r?.server && Array.isArray(r?.completion_tiers) && r?.completion_tiers?.length > 0) {
        map[r.server] = r?.completion_tiers;
      }
    });
    return map;
  }, [incrementalConfig]);

  const managerPerfData = useMemo(() => {
    const managerPerf = {};
    filteredData?.forEach((r) => {
      const key = r?.manager_id || 'unassigned';
      if (!managerPerf?.[key]) {
        managerPerf[key] = { name: r?.manager_name || t('common.unassigned'), diamonds: 0, active_creators: 0, bonus_total: 0 };
      }
      managerPerf[key].diamonds += r?.diamonds || 0;
      managerPerf[key].bonus_total += (r?.bonus_rankup || 0) + (r?.bonus_activeness || 0);
      if (r?.is_active) managerPerf[key].active_creators++;
    });
    return Object.values(managerPerf)?.filter((m) => m?.diamonds > 0)?.sort((a, b) => b?.diamonds - a?.diamonds);
  }, [filteredData]);

  const getServerColor = useCallback((serverId) => {
    const srv = serversData?.find((s) => s?.id === serverId);
    return srv?.color || '#E5202A';
  }, [serversData]);

  const serverColor = selectedServer === 'Tous' ? '#E5202A' : getServerColor(selectedServer);

  const periodDisplay = useMemo(() => {
    if (!currentPeriod) return '—';
    const [year, month] = currentPeriod?.split('-');
    const months = [t('months.0'), t('months.1'), t('months.2'), t('months.3'), t('months.4'), t('months.5'), t('months.6'), t('months.7'), t('months.8'), t('months.9'), t('months.10'), t('months.11')];
    return `${months?.[parseInt(month, 10) - 1] || month} ${year}`;
  }, [currentPeriod]);

  const serverTabs = useMemo(() => {
    if (allowedServers?.length <= 1) return allowedServers;
    return ['Tous', ...allowedServers];
  }, [allowedServers]);

  const pageTitle = managerProfile?.name ? t('region.title', { name: managerProfile?.name }) : t('region.mobileTitle');

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: 'var(--color-background)', fontFamily: 'Inter, sans-serif' }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Sora:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <ManagerSidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} managerName={managerProfile?.name || ''} />
      <main className="flex-1 overflow-y-auto min-w-0 pb-16 md:pb-0">
        <ManagerMobileHeader title={t('region.mobileTitle')} showBack={false} />

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 md:px-6 py-4 border-b" style={{ backgroundColor: 'var(--color-background)', borderColor: '#1f1f1f' }}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/manager/dashboard')}
              className="flex items-center gap-2 transition-colors hover:text-white"
              style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}
            >
              <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
                <path d="M9 3L5 7l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="hidden md:inline">{t('common.back')}</span>
            </button>
            <div>
              <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{pageTitle}</h1>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{periodDisplay} — {t('region.realtimeData', 'real-time data')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {allowedServers?.map(srv => (
              <span key={srv} className="text-xs font-bold px-2 py-0.5 rounded-md" style={{ backgroundColor: 'rgba(229,32,42,0.12)', color: '#E5202A', fontFamily: 'Sora, sans-serif' }}>
                {srv}
              </span>
            ))}
            <div className="px-3 py-1.5 rounded-lg text-xs font-semibold border" style={{ backgroundColor: 'rgba(229,32,42,0.08)', borderColor: 'rgba(229,32,42,0.2)', color: '#E5202A', fontFamily: 'Sora, sans-serif' }}>
              {periodDisplay}
            </div>
          </div>
        </div>

        {/* Server Tabs */}
        <div className="px-4 md:px-6 py-3 border-b flex items-center gap-2 flex-wrap" style={{ borderColor: '#1f1f1f', backgroundColor: '#0a0a0a' }}>
          <span className="text-xs font-medium mr-2" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>{t('region.serverLabel')}</span>
          {serverTabs?.map(srv => {
            const isActive = selectedServer === srv;
            const color = srv === 'Tous' ? '#E5202A' : getServerColor(srv);
            return (
              <button
                key={srv}
                onClick={() => setSelectedServer(srv)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold tracking-wider transition-all"
                style={{
                  backgroundColor: isActive ? `${color}20` : 'transparent',
                  color: isActive ? color : 'rgba(255,255,255,0.4)',
                  border: `1px solid ${isActive ? color + '50' : '#2a2a2a'}`,
                  fontFamily: 'Sora, sans-serif',
                }}
              >
                {srv === 'Tous' ? t('common.all', 'All') : srv}
              </button>
            );
          })}
        </div>

        {/* Filter Bar — Manager + Level */}
        <div className="px-4 md:px-6 py-3 border-b flex items-center gap-4 flex-wrap" style={{ borderColor: '#1f1f1f', backgroundColor: '#0a0a0a' }}>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>{t('region.levelLabel')}</span>
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e?.target?.value)}
              className="rounded-lg px-3 py-1.5 text-base outline-none"
              style={{ backgroundColor: '#1a1a1a', border: `1px solid ${selectedLevel ? '#E5202A50' : '#2a2a2a'}`, color: selectedLevel ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif', minWidth: '140px' }}
            >
              <option value="">{t('region.allLevels')}</option>
              <option value="Junior">Junior</option>
              <option value="Rising">Rising</option>
              <option value="Senior">Senior</option>
              <option value="Elite">Elite</option>
            </select>
          </div>
          <div style={{ width: '1px', height: '20px', backgroundColor: '#2a2a2a' }} />
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>{t('region.managerLabel')}</span>
            <select
              value={selectedManagerId}
              onChange={(e) => setSelectedManagerId(e?.target?.value)}
              className="rounded-lg px-3 py-1.5 text-base outline-none"
              style={{ backgroundColor: '#1a1a1a', border: `1px solid ${selectedManagerId ? '#E5202A50' : '#2a2a2a'}`, color: selectedManagerId ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif', minWidth: '180px' }}
            >
              <option value="">{t('region.allManagers')}</option>
              {managers?.map((m) => (
                <option key={m?.id} value={m?.id}>{m?.name}</option>
              ))}
            </select>
            {selectedManagerId && (
              <button
                onClick={() => setSelectedManagerId('')}
                className="text-xs px-2 py-1 rounded"
                style={{ color: 'rgba(255,255,255,0.4)', backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a' }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 md:p-6 space-y-8">
          {allowedServers?.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="text-4xl mb-4">🌍</div>
                <p className="text-white/50 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('region.noServersAssigned')}</p>
              </div>
            </div>
          ) : (
            <>
              {/* SECTION 1 — Summary Cards */}
              <section>
                <SectionHeader color={serverColor} label={t('region.summary', { period: periodDisplay })} />
                <SummaryCards summary={summary} serverColor={serverColor} loading={loading} />
                <div className="mt-3 flex gap-3 flex-wrap">
                  <div className="rounded-xl p-3 border inline-flex flex-col" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', minWidth: '160px' }}>
                    <p className="text-xs uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{t('region.newRecruits')}</p>
                    {loading ? (
                      <div className="h-5 w-12 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f' }} />
                    ) : (
                      <p className="text-lg font-bold" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>{summary?.new_recruits}</p>
                    )}
                    <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}>{t('region.contractsThisMonth')}</p>
                  </div>
                  <div className="rounded-xl p-3 border inline-flex flex-col" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', minWidth: '160px' }}>
                    <p className="text-xs uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{t('region.estimatedCashback')}</p>
                    {loading ? (
                      <div className="h-5 w-12 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f' }} />
                    ) : (
                      <p className="text-lg font-bold" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>
                        ${filteredData?.reduce((sum, r) => sum + (parseFloat(r?.cashback_amount) || 0), 0)?.toFixed(2)}
                      </p>
                    )}
                    <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}>{t('region.currentMonth')}</p>
                  </div>
                </div>
              </section>

              {/* SECTION 2 — Objectif Incremental par Serveur */}
              <section>
                <SectionHeader color={serverColor} label={t('region.incrementalObjective', { period: periodDisplay })} />
                <ServerObjectivesSection
                  serversData={serversData}
                  selectedServer={selectedServer}
                  currentData={filteredData}
                  targets={targets}
                  paliersMap={paliersMap}
                  fallbackPaliers={FALLBACK_PALIERS}
                  loading={loading || configLoading}
                />
              </section>

              {/* SECTION 3 — Objectifs À Date */}
              <section>
                <SectionHeader color="#E5202A" label={t('region.objectivesToDate', { period: periodDisplay })} />
                <ObjectifsADateSection
                  currentData={filteredData}
                  serversData={serversData}
                  selectedServer={selectedServer}
                  targets={targets}
                  incrementalConfig={incrementalConfig}
                  fallbackPaliers={FALLBACK_PALIERS}
                  loading={loading}
                />
              </section>

              {/* SECTION 4 — Objectifs Managers (read-only) */}
              <section>
                <SectionHeader color="#a855f7" label={t('region.managerObjectives', { period: periodDisplay })} />
                <ManagerObjectivesTable
                  managerObjectives={managerObjectives}
                  currentData={filteredData}
                  serversData={serversData}
                  selectedServer={selectedServer}
                  loading={loading || configLoading}
                />
              </section>

              {/* SECTION 5 — Tendance Journalière */}
              <section>
                <SectionHeader color={serverColor} label={t('region.dailyTrend')} />
                <PerfDailyTrendChart data={dailyTrendData} serverColor={serverColor} loading={loading} />
              </section>

              {/* SECTION 6 — Comparaison Managers */}
              <section>
                <SectionHeader color={serverColor} label={t('region.managerComparison')} />
                <ManagerComparisonChart managerPerfData={managerPerfData} serverColor={serverColor} loading={loading} />
              </section>

              {/* SECTION 7 — Opportunités Bonus */}
              <section>
                <SectionHeader color={serverColor} label={t('region.bonusOpportunities', 'Bonus Opportunities')} />
                <OpportunitesBonusSection
                  data={filteredData}
                  loading={loading}
                  serverColor={serverColor}
                  basePath="/manager/creators"
                />
              </section>

              {/* SECTION 8 — Table Créateurs */}
              <section>
                <SectionHeader color={serverColor} label={t('region.creatorPerformance')} />
<CreatorCurrentTable
  data={filteredData}
  loading={loading}
  serverColor={serverColor}
  getServerColor={getServerColor}
  basePath="/manager/creators"
/>
              </section>
            </>
          )}
        </div>
      </main>
      <ManagerBottomNav />
    </div>
  );
};

export default ManagerRegionPerformance;
